import './confirmDialog.scss';

import { ReactNode, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, ButtonColor, ButtonVariant, Size } from '@eliberty/design-system-react';

import { ResponsiveSize } from '~constants';
import { useMediaQuery } from '~hooks';

export interface ConfirmDialogPreview {
  category?: string;
  title?: string;
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  preview?: ConfirmDialogPreview;
  variant?: 'danger' | 'primary';
  onCancel: () => void;
  onConfirm: () => void;
}

const WarningIcon = () => (
  <svg
    aria-hidden
    fill='none'
    height='20'
    stroke='currentColor'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth='1.8'
    viewBox='0 0 24 24'
    width='20'
  >
    <path d='M12 3l10 17H2L12 3z' />
    <path d='M12 10v4M12 17v.5' />
  </svg>
);

const ConfirmDialog = ({
  body,
  confirmLabel,
  isOpen,
  onCancel,
  onConfirm,
  preview,
  title,
  variant = 'danger',
}: ConfirmDialogProps) => {
  const isMobile = useMediaQuery(ResponsiveSize.SCREEN_S_MOBILE);
  const buttonSize = isMobile ? Size.S : Size.M;
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    confirmButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      className={`ConfirmDialog_overlay${isMobile ? ' ConfirmDialog_overlay-sheet' : ''}`}
      role='none'
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <dialog aria-modal='true' className='ConfirmDialog_dialog' open>
        <div className='ConfirmDialog_head'>
          <div
            aria-hidden
            className={`ConfirmDialog_iconWrapper ConfirmDialog_iconWrapper-${variant}`}
          >
            <WarningIcon />
          </div>
          <h2 className='ConfirmDialog_title'>{title}</h2>
        </div>
        <p className='ConfirmDialog_body'>{body}</p>
        {preview && (preview.category || preview.title) && (
          <div className='ConfirmDialog_preview'>
            {preview.category && (
              <span className='ConfirmDialog_previewCategory'>{preview.category}</span>
            )}
            {preview.title && (
              <span className='ConfirmDialog_previewTitle'>{preview.title}</span>
            )}
          </div>
        )}
        <div className='ConfirmDialog_actions'>
          <Button
            color={ButtonColor.Black}
            onClick={onCancel}
            size={buttonSize}
            variant={ButtonVariant.Outlined}
          >
            <FormattedMessage defaultMessage='Cancel' id='cartDialogCancel' />
          </Button>
          <Button
            color={variant === 'danger' ? ButtonColor.Negative : ButtonColor.Primary}
            onClick={onConfirm}
            ref={confirmButtonRef}
            size={buttonSize}
            variant={ButtonVariant.Filled}
          >
            {confirmLabel}
          </Button>
        </div>
      </dialog>
    </div>
  );
};

export default ConfirmDialog;
